import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useSpring, useInView } from 'framer-motion';
import { ArrowRight, Star, Globe, Shield, UserCheck, Lock, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useParallax, useCounter, staggerContainer, staggerItem, scaleIn, slideLeft, slideRight } from '../hooks/useAnimations';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/7621458b-ca63-4125-ad1b-115cf548682c/images/e2bf7539380232c158edb21ac1619c8ff9791a16d08714d3b35212123f73cd7b.png';
const HERO_VIDEO = 'https://assets.mixkit.co/videos/45780/45780-720.mp4';
const TEXTURE_IMG = 'https://static.prod-images.emergentagent.com/jobs/7621458b-ca63-4125-ad1b-115cf548682c/images/2a9c165df02cfa0e34df8e8ab7f95ecb8f3d2d03413c30dcf4c8416755cf1aac.png';

const TESTIMONIALS = [
  { text: "No fue un viaje de golf. Fue la experiencia mas extraordinaria de mi vida.", name: "Carlos M.", role: "CEO & Golfista" },
  { text: "Cada detalle fue impecable. Acceso que no creia posible. Nivel incomparable.", name: "Roberto G.", role: "Empresario" },
  { text: "Discrecion, elegancia y campos que solo habia sonado jugar. Simplemente elite.", name: "Fernando L.", role: "Inversionista" },
];

const MARQUEE = ['CONCIERGE PRIVADO', 'ACCESO EXCLUSIVO', 'CAMPOS LEGENDARIOS', 'EXPERIENCIAS A MEDIDA', 'SERVICIO WHITE-GLOVE', 'GOLF DE CLASE MUNDIAL'];

const EXP_CATEGORIES = [
  { title: 'Campeonatos & Eventos VIP', desc: 'Acceso privilegiado a los eventos mas exclusivos del golf mundial.', img: 'https://images.pexels.com/photos/8454632/pexels-photo-8454632.jpeg?auto=compress&cs=tinysrgb&w=800' },
  { title: 'Campos Legendarios', desc: 'Los campos mas iconicos del planeta, reservados para ti.', img: 'https://images.unsplash.com/photo-1672871583040-42826d4e9ca4?q=80&w=1200&auto=format&fit=crop' },
  { title: 'Retiros Privados', desc: 'Escapadas de golf disenadas con absoluta privacidad.', img: 'https://static.prod-images.emergentagent.com/jobs/7621458b-ca63-4125-ad1b-115cf548682c/images/ca7cf37d3af34a14ef9535c7ef238bf0f220cc3b1fb9d3f8b6f6a609b29b6d56.png' },
  { title: 'Golf Corporativo', desc: 'Retiros ejecutivos que combinan negocios y golf de primer nivel.', img: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=1200&auto=format&fit=crop' },
  { title: 'Padre e Hijo', desc: 'Momentos inolvidables compartiendo la pasion por el golf.', img: 'https://images.pexels.com/photos/33245231/pexels-photo-33245231.jpeg?auto=compress&cs=tinysrgb&w=800' },
  { title: 'Golf & Lifestyle', desc: 'Golf, gastronomia, yates y experiencias que trascienden el juego.', img: 'https://static.prod-images.emergentagent.com/jobs/7621458b-ca63-4125-ad1b-115cf548682c/images/474fcaae205b7391782b6f63001d9cad475a62c831d1dc12ea6e58f1abe5cbc8.png' },
];

function ParallaxImage({ src, alt, className, speed = 60 }) {
  const { ref, y } = useParallax(speed);
  return (
    <div ref={ref} className={`overflow-hidden ${className || ''}`}>
      <motion.img src={src} alt={alt} style={{ y }} className="w-full h-[120%] object-cover" loading="lazy" />
    </div>
  );
}

function CounterStat({ end, suffix = '', label }) {
  const { ref, count } = useCounter(end);
  return (
    <div ref={ref} className="text-center">
      <p className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
        {count}{suffix}
      </p>
      <p className="text-sm text-zinc-500 mt-2">{label}</p>
    </div>
  );
}

export default function HomePage() {
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const [subForm, setSubForm] = useState({ name: '', email: '', country: '', handicap: '' });
  const [subSending, setSubSending] = useState(false);

  const [videoLoaded, setVideoLoaded] = useState(false);

  // Hero parallax
  const heroRef = useRef(null);
  const { scrollYProgress: heroScroll } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(heroScroll, [0, 1], [0, 300]);
  const heroOpacity = useTransform(heroScroll, [0, 0.8], [1, 0]);
  const heroScale = useTransform(heroScroll, [0, 1], [1, 1.15]);
  const smoothHeroY = useSpring(heroY, { stiffness: 50, damping: 20 });

  useEffect(() => {
    const interval = setInterval(() => setTestimonialIdx(i => (i + 1) % TESTIMONIALS.length), 5000);
    return () => clearInterval(interval);
  }, []);

  const handleSubscription = async (e) => {
    e.preventDefault();
    if (!subForm.name || !subForm.email) { toast.error('Nombre y email requeridos'); return; }
    setSubSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscriptions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(subForm)
      });
      const data = await res.json();
      toast.success(data.message);
      setSubForm({ name: '', email: '', country: '', handicap: '' });
    } catch { toast.error('Error al suscribirse'); }
    finally { setSubSending(false); }
  };

  return (
    <div data-testid="home-page">
      {/* HERO with Video + Parallax fallback */}
      <section ref={heroRef} className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Video background */}
        <motion.video
          autoPlay muted loop playsInline
          poster={HERO_IMG}
          onCanPlay={() => setVideoLoaded(true)}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ y: smoothHeroY, scale: heroScale }}
          data-testid="hero-video"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </motion.video>
        {/* Fallback image while video loads */}
        {!videoLoaded && (
          <motion.img src={HERO_IMG} alt="Elite Golf" className="absolute inset-0 w-full h-full object-cover"
            style={{ y: smoothHeroY, scale: heroScale }} />
        )}
        <div className="hero-overlay absolute inset-0" />
        <motion.div className="relative z-10 text-center px-6 max-w-4xl" style={{ opacity: heroOpacity }}>
          <motion.p initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }}
            className="overline mb-6">Private Golf Concierge</motion.p>
          <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 1.2 }}
            className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter text-white leading-[0.95] overflow-hidden"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            <motion.span className="inline-block" initial={{ y: 80 }} animate={{ y: 0 }} transition={{ delay: 0.6, duration: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}>
              Experiencias de Golf Elite.
            </motion.span>
            <br />
            <motion.span className="inline-block" initial={{ y: 80 }} animate={{ y: 0 }} transition={{ delay: 0.75, duration: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}>
              En Cualquier Lugar del Mundo.
            </motion.span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 0.8 }}
            className="text-base md:text-lg text-zinc-400 mt-6 max-w-xl mx-auto">
            Viajes de golf personalizados, acceso privado y experiencias inolvidables curadas por especialistas elite.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5, duration: 0.8 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contacto" className="bg-white text-black px-8 py-4 text-sm font-medium hover:bg-zinc-200 transition-colors tracking-wide" data-testid="hero-cta-request">
              Solicitar Experiencia
            </Link>
            <a href="https://wa.me/524442049874?text=Hola,%20me%20interesa%20una%20experiencia%20de%20golf%20personalizada"
              target="_blank" rel="noreferrer"
              className="border border-white/20 text-white px-8 py-4 text-sm font-medium hover:bg-white/10 transition-colors tracking-wide" data-testid="hero-cta-concierge">
              Hablar con Concierge
            </a>
          </motion.div>
        </motion.div>
        {/* Scroll indicator */}
        <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2, duration: 0.8 }}>
          <motion.div className="w-5 h-8 border border-white/20 rounded-full flex justify-center pt-1.5"
            animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}>
            <motion.div className="w-1 h-2 bg-white/40 rounded-full" />
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <div className="bg-[#050505] border-y border-white/5 py-4 overflow-hidden">
        <div className="marquee-track flex items-center gap-12 whitespace-nowrap">
          {[...MARQUEE, ...MARQUEE].map((item, i) => (
            <span key={`marquee-${i}`} className="text-xs tracking-[0.3em] text-zinc-600 font-medium flex items-center gap-4">
              <span className="w-1.5 h-1.5 bg-zinc-700 rounded-full" /> {item}
            </span>
          ))}
        </div>
      </div>

      {/* GLOBAL EXPERIENCES - Staggered Grid */}
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="global-experiences">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }}
            variants={staggerContainer} className="text-center mb-16">
            <motion.p variants={staggerItem} className="overline mb-3">Experiencias Globales</motion.p>
            <motion.h2 variants={staggerItem} className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Disenamos lo Extraordinario
            </motion.h2>
            <motion.p variants={staggerItem} className="text-base text-zinc-400 mt-4 max-w-lg mx-auto">Cada experiencia es unica. Cada detalle, personalizado.</motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: '-50px' }}
            variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {EXP_CATEGORIES.map((cat) => (
              <motion.div key={cat.title} variants={scaleIn}>
                <Link to="/experiencias" className="group block relative aspect-[4/3] overflow-hidden bg-[#0A120E]" data-testid={`exp-cat-${cat.title.toLowerCase().replace(/ /g, '-')}`}>
                  <img src={cat.img} alt={cat.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent transition-all duration-500 group-hover:from-black/95" />
                  <div className="absolute bottom-0 left-0 p-6">
                    <h3 className="text-lg font-bold text-white tracking-tight mb-1 transition-transform duration-500 group-hover:translate-x-2">{cat.title}</h3>
                    <p className="text-sm text-zinc-400 leading-relaxed transition-all duration-500 opacity-80 group-hover:opacity-100">{cat.desc}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS - Staggered Steps */}
      <section className="py-24 md:py-32 bg-[#0A120E]" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }}
            variants={staggerContainer} className="text-center mb-16">
            <motion.p variants={staggerItem} className="overline mb-3">Como Funciona</motion.p>
            <motion.h2 variants={staggerItem} className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Tu Experiencia, Paso a Paso
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: '-50px' }}
            variants={staggerContainer} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { num: '01', title: 'Cuentanos Tu Vision', desc: 'Describe tu experiencia de golf ideal. Sin limites, sin restricciones.' },
              { num: '02', title: 'Diseno Personalizado', desc: 'Nuestro equipo crea un itinerario a medida con cada detalle cubierto.' },
              { num: '03', title: 'Ejecucion Concierge', desc: 'Coordinamos cada aspecto con precision y discrecion absoluta.' },
              { num: '04', title: 'Golf Inolvidable', desc: 'Vive una experiencia que superara todas tus expectativas.' },
            ].map((step) => (
              <motion.div key={step.num} variants={staggerItem}
                className="text-left p-8 border border-white/5 hover:border-white/15 transition-all duration-500 group">
                <span className="text-4xl font-bold text-zinc-800 block mb-4 transition-colors duration-500 group-hover:text-zinc-600" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{step.num}</span>
                <h3 className="text-base font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* WHY US - Split layout with parallax image */}
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="why-us">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }}
              variants={staggerContainer}>
              <motion.p variants={slideLeft} className="overline mb-4">Por Que Elite Golf MX</motion.p>
              <motion.h2 variants={slideLeft} className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                El Concierge de Golf<br />Mas Exclusivo.
              </motion.h2>
              <motion.p variants={slideLeft} className="text-base text-zinc-400 mt-6 leading-relaxed max-w-lg">
                No somos una agencia de viajes. Somos un servicio de concierge privado dedicado a crear
                las experiencias de golf mas extraordinarias del mundo.
              </motion.p>
              <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={staggerContainer}
                className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-10">
                {[
                  { icon: Globe, title: 'Red Global', desc: 'Acceso a los campos mas exclusivos en cada continente.' },
                  { icon: Shield, title: 'Acceso VIP', desc: 'Horarios privados y acceso que otros no pueden ofrecer.' },
                  { icon: UserCheck, title: 'Personalizado', desc: 'Cada experiencia disenada desde cero para ti.' },
                  { icon: Lock, title: 'Discrecion Total', desc: 'Privacidad y confidencialidad en cada interaccion.' },
                ].map((item) => (
                  <motion.div key={item.title} variants={staggerItem} className="space-y-2">
                    <item.icon className="w-5 h-5 text-zinc-500" strokeWidth={1.5} />
                    <h3 className="text-sm font-semibold text-white">{item.title}</h3>
                    <p className="text-xs text-zinc-500 leading-relaxed">{item.desc}</p>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={{ hidden: { opacity: 0, x: 60 }, show: { opacity: 1, x: 0, transition: { duration: 0.9, ease: [0.25, 0.46, 0.45, 0.94] } } }}>
              <ParallaxImage
                src="https://images.unsplash.com/photo-1672871583062-7613925d0734?q=80&w=1200&auto=format&fit=crop"
                alt="Links Golf Course"
                className="relative aspect-[4/5] bg-[#0A120E]"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* STATS COUNTER */}
      <section className="py-20 bg-[#0A120E] border-y border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-3 gap-8">
            <CounterStat end={50} suffix="+" label="Campos Exclusivos" />
            <CounterStat end={500} suffix="+" label="Experiencias Creadas" />
            <CounterStat end={15} suffix="+" label="Anos de Excelencia" />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="testimonials">
        <img src={TEXTURE_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-[#050505]/85" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <p className="overline mb-8">Lo Que Dicen Nuestros Clientes</p>
            <motion.p key={testimonialIdx}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6 }}
              className="text-2xl md:text-3xl lg:text-4xl font-light text-white leading-relaxed tracking-tight italic" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              &ldquo;{TESTIMONIALS[testimonialIdx].text}&rdquo;
            </motion.p>
            <motion.div key={`name-${testimonialIdx}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.5 }} className="mt-8">
              <p className="text-sm font-semibold text-white">{TESTIMONIALS[testimonialIdx].name}</p>
              <p className="text-xs text-zinc-500 mt-1">{TESTIMONIALS[testimonialIdx].role}</p>
            </motion.div>
            <div className="flex items-center justify-center gap-2 mt-6">
              {TESTIMONIALS.map((_, i) => (
                <button key={i} onClick={() => setTestimonialIdx(i)}
                  className={`h-2 rounded-full transition-all duration-500 ${i === testimonialIdx ? 'bg-white w-8' : 'bg-white/20 w-2'}`}
                  data-testid={`testimonial-dot-${i}`} />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* SUBSCRIPTION - ELITE GOLF CIRCLE */}
      <section className="py-24 md:py-32 bg-[#0A120E]" data-testid="subscription-section">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={staggerContainer}>
            <motion.div variants={staggerItem}><Star className="w-6 h-6 text-zinc-500 mx-auto mb-4" strokeWidth={1.5} /></motion.div>
            <motion.h2 variants={staggerItem} className="text-3xl md:text-4xl font-bold tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Unete al Circulo Elite Golf
            </motion.h2>
            <motion.p variants={staggerItem} className="text-base text-zinc-400 mt-4 mb-10 max-w-md mx-auto">
              Recibe invitaciones privadas, experiencias limitadas y acceso anticipado a oportunidades exclusivas.
            </motion.p>
            <motion.form variants={staggerItem} onSubmit={handleSubscription} className="space-y-4 text-left" data-testid="subscription-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input type="text" placeholder="Nombre *" value={subForm.name} onChange={e => setSubForm(f => ({ ...f, name: e.target.value }))}
                  className="bg-white/5 border border-white/10 px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30 transition-colors duration-300" data-testid="sub-name" />
                <input type="email" placeholder="Email *" value={subForm.email} onChange={e => setSubForm(f => ({ ...f, email: e.target.value }))}
                  className="bg-white/5 border border-white/10 px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30 transition-colors duration-300" data-testid="sub-email" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input type="text" placeholder="Pais" value={subForm.country} onChange={e => setSubForm(f => ({ ...f, country: e.target.value }))}
                  className="bg-white/5 border border-white/10 px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30 transition-colors duration-300" data-testid="sub-country" />
                <input type="text" placeholder="Handicap (opcional)" value={subForm.handicap} onChange={e => setSubForm(f => ({ ...f, handicap: e.target.value }))}
                  className="bg-white/5 border border-white/10 px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30 transition-colors duration-300" data-testid="sub-handicap" />
              </div>
              <button type="submit" disabled={subSending}
                className="w-full bg-white text-black py-4 text-sm font-medium hover:bg-zinc-200 transition-colors disabled:opacity-50" data-testid="sub-submit">
                {subSending ? 'Procesando...' : 'Unirse al Circulo'}
              </button>
            </motion.form>
          </motion.div>
        </div>
      </section>

      {/* CONCIERGE CTA */}
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="concierge-cta">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={staggerContainer}>
            <motion.h2 variants={staggerItem} className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Tu Proxima Ronda Legendaria<br />Comienza Aqui.
            </motion.h2>
            <motion.p variants={staggerItem} className="text-base text-zinc-400 mt-6 max-w-md mx-auto">
              Cada experiencia es disenada personalmente. Cuentanos tu vision.
            </motion.p>
            <motion.div variants={staggerItem} className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
              <Link to="/contacto" className="bg-white text-black px-8 py-4 text-sm font-medium hover:bg-zinc-200 transition-colors inline-flex items-center gap-2" data-testid="cta-request">
                Solicitar Experiencia Personalizada <ArrowRight size={16} />
              </Link>
              <a href="https://wa.me/524442049874?text=Hola,%20me%20interesa%20una%20experiencia%20de%20golf%20personalizada"
                target="_blank" rel="noreferrer"
                className="border border-white/20 text-white px-8 py-4 text-sm font-medium hover:bg-white/10 transition-colors inline-flex items-center gap-2" data-testid="cta-whatsapp">
                <MessageCircle size={16} /> WhatsApp Concierge
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
